import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler);

interface Device {
  id: number;
  name: string;
  status: 0 | 1;
  socketConnected?: boolean;
  deviceTypes?: { id: number; name: string };
}

interface RestDeviceCardProps {
  device: Device;
  deviceMetrics: Record<number, Record<string, any[]>>;
  themeColor?: string;
}

const REST_METRICS_CONFIG: Record<string, { label: string; unit: string; color: string; decimals: number }> = {
  temp_c:   { label: 'Temperature', unit: '°C', color: '#ff9800', decimals: 1 },
  level_mm: { label: 'Level',       unit: 'mm', color: '#00bcd4', decimals: 1 },
  mA_temp:  { label: 'Temp mA',     unit: 'mA', color: '#ffb74d', decimals: 2 },
  mA_lvl:   { label: 'Level mA',    unit: 'mA', color: '#4dd0e1', decimals: 2 },
  fault:    { label: 'Fault',       unit: '',   color: '#f44336', decimals: 0 },
};

const CHART_GROUPS = [
  { key: 'temperature', label: 'Temperature', unit: '°C', color: '#ff9800', metrics: ['temp_c'] },
  { key: 'level',       label: 'Level',       unit: 'mm', color: '#00bcd4', metrics: ['level_mm'] },
  { key: 'current',     label: 'Current',     unit: 'mA', color: '#ffb74d', metrics: ['mA_temp', 'mA_lvl'] },
];

const SPARKLINE_POINTS = 30;

const RestDeviceCard: React.FC<RestDeviceCardProps> = ({
  device,
  deviceMetrics,
  themeColor = '#00bcd4',
}) => {
  const metricsData = useMemo(
    () => deviceMetrics[device.id] || {},
    [deviceMetrics, device.id]
  );

  const latestFault = useMemo(() => {
    const arr = metricsData['fault'];
    return Array.isArray(arr) && arr.length > 0 ? arr[arr.length - 1] : null;
  }, [metricsData]);

  const latestTank = useMemo(() => {
    const arr = metricsData['tank'];
    return Array.isArray(arr) && arr.length > 0 ? arr[arr.length - 1] : null;
  }, [metricsData]);

  const chartGroups = useMemo(() => {
    return CHART_GROUPS.flatMap(group => {
      const datasets: any[] = [];

      group.metrics.forEach(key => {
        const data = metricsData[key];
        if (!Array.isArray(data) || data.length === 0) return;
        const cfg = REST_METRICS_CONFIG[key];
        datasets.push({
          label: cfg.label,
          data: data.slice(-SPARKLINE_POINTS),
          borderColor: cfg.color,
          backgroundColor: `${cfg.color}20`,
          borderWidth: 1.5,
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 2,
        });
      });

      if (datasets.length === 0) return [];
      const pointCount = Math.max(...datasets.map((d: any) => d.data.length));
      return [{
        key: group.key,
        label: group.label,
        unit: group.unit,
        color: group.color,
        data: { labels: Array(pointCount).fill(''), datasets },
      }];
    });
  }, [metricsData]);

  const readingTiles = useMemo(() => {
    return Object.entries(REST_METRICS_CONFIG)
      .filter(([key]) => key !== 'fault')
      .map(([key, cfg]) => {
        const arr = metricsData[key];
        const latest = Array.isArray(arr) && arr.length > 0 ? arr[arr.length - 1] : null;
        return { key, cfg, latest };
      })
      .filter(({ latest }) => latest !== null);
  }, [metricsData]);

  const chartOptions = (unit: string) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: { boxWidth: 6, boxHeight: 6, padding: 4, font: { size: 8 }, color: '#888' },
      },
      tooltip: {
        enabled: true,
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: (ctx: any) => `${ctx.dataset.label}: ${ctx.raw?.toFixed(1) ?? '0'}${unit}`,
        },
      },
    },
    scales: {
      x: { display: false },
      y: {
        display: true,
        grid: { color: 'rgba(255,255,255,0.05)' },
        ticks: { font: { size: 7 }, color: '#666', maxTicksLimit: 3 },
      },
    },
    interaction: { mode: 'nearest' as const, axis: 'x' as const, intersect: false },
  });

  const hasData = readingTiles.length > 0 || chartGroups.length > 0;

  return (
    <div style={{
      background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
      border: `1px solid ${themeColor}30`,
      borderRadius: '12px',
      padding: '1rem',
      minWidth: '280px',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '1rem',
        paddingBottom: '0.75rem',
        borderBottom: `1px solid ${themeColor}20`,
      }}>
        <div style={{ fontSize: '1.5rem' }}>🌡️</div>
        <div style={{
          width: '10px', height: '10px', borderRadius: '50%',
          backgroundColor: device.socketConnected ? '#00ff88' : '#ff3366',
          boxShadow: device.socketConnected ? '0 0 8px #00ff88' : '0 0 8px #ff3366',
        }} />
        <div>
          <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#fff' }}>{device.name}</div>
          <div style={{ fontSize: '0.7rem', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            REST Device
          </div>
        </div>
        {latestFault !== null && (
          <div style={{
            fontSize: '0.6rem',
            fontWeight: 700,
            padding: '2px 6px',
            borderRadius: '4px',
            backgroundColor: latestFault ? '#f4433620' : '#00ff8820',
            border: `1px solid ${latestFault ? '#f44336' : '#00ff88'}50`,
            color: latestFault ? '#f44336' : '#00ff88',
          }}>
            {latestFault ? 'FAULT' : 'OK'}
          </div>
        )}
        {latestTank !== null && (
          <div style={{
            fontSize: '0.6rem',
            fontWeight: 700,
            padding: '2px 6px',
            borderRadius: '4px',
            backgroundColor: `${themeColor}20`,
            border: `1px solid ${themeColor}50`,
            color: themeColor,
          }}>
            {latestTank}
          </div>
        )}
      </div>

      {hasData ? (
        <>
          {/* Reading tiles */}
          {readingTiles.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(70px, 1fr))',
              gap: '0.35rem',
              marginBottom: '0.75rem',
            }}>
              {readingTiles.map(({ key, cfg, latest }) => (
                <div key={key} style={{
                  background: `${cfg.color}10`,
                  border: `1px solid ${cfg.color}30`,
                  borderRadius: '6px',
                  padding: '0.4rem 0.5rem',
                }}>
                  <div style={{
                    fontSize: '0.55rem', color: cfg.color, textTransform: 'uppercase',
                    letterSpacing: '0.3px', whiteSpace: 'nowrap', overflow: 'hidden',
                    textOverflow: 'ellipsis', marginBottom: '2px',
                  }}>
                    {cfg.label}
                  </div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#fff', fontFamily: 'monospace' }}>
                    {(latest as number).toFixed(cfg.decimals)}
                    <span style={{ fontSize: '0.55rem', opacity: 0.7, marginLeft: '2px' }}>{cfg.unit}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Charts */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: chartGroups.length > 1 ? 'repeat(2, 1fr)' : '1fr',
            gap: '0.5rem',
          }}>
            {chartGroups.map(group => (
              <div key={group.key} style={{
                background: 'rgba(0,0,0,0.2)',
                borderRadius: '8px',
                padding: '0.5rem',
                border: `1px solid ${group.color}20`,
              }}>
                <div style={{
                  fontSize: '0.55rem', color: group.color, textTransform: 'uppercase',
                  fontWeight: 600, letterSpacing: '0.5px', marginBottom: '0.25rem',
                }}>
                  {group.label}
                </div>
                <div style={{ height: '70px' }}>
                  <Line data={group.data} options={chartOptions(group.unit)} />
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', color: '#6c757d', fontSize: '0.85rem', padding: '1rem' }}>
          No metrics available
        </div>
      )}
    </div>
  );
};

export default RestDeviceCard;
